import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  ClientSettings,
  DocumentTemplate,
  Eviction,
  Organization,
  User,
  Workflow,
  WorkflowStep
} from '@ee/common/models';
import { AccountType } from '@ee/common/enums';
import { ClientSelectComponent } from '@ee/common/client-selection';
import { ClientService, FormService, UserService } from '@ee/common/services';
import { Observable, Subscription, take } from 'rxjs';
import { IntakeEvictionFormService } from '../services/intake-eviction-form.service';

@Component({
  selector: 'ee-workflow-form',
  template: `
    <div class="flex flex-1 flex-col" [formGroup]="this.intakeEvictionFormService.workflowDetails">
      @if (this.intakeEvictionFormService.loggedInOrgType === accountTypes.ATTORNEY) {
        <ee-expansion-panel header="Client" [collapsible]="false" class="mb-4">
          @if (hasClients) {
            <div>Select the client who requested the eviction.</div>
          }
          <div class="mt-2 flex flex-col">
            @if (hasClients) {
              <ee-client-select
                #clientSelect
                [initClientId]="preSelectedClientId ?? selectedClient?.id"
                [fields]="'street_address_1,city,state,zip_code,property_name'"
                [disabled]="!!cloneCase"
                (selectedClientChange)="selectClient($event)"
                (clientCount)="updateClientCount($event)"></ee-client-select>
            } @else {
              @if (!hasClients) {
                <div class="card-content no-top-padding flex flex-col justify-center items-center">
                  <p class="mb-4">
                    Welcome to EasyEviction! Let's set up a new client so you can file your first eviction.
                  </p>
                  <button mat-flat-button color="accent" (click)="handleAddClient()">Add Client</button>
                </div>
              }
            }
          </div>
        </ee-expansion-panel>
      }

      @if (intakeEvictionFormService.loggedInOrgType === accountTypes.ATTORNEY) {
        <ee-expansion-panel header="Workflow" [collapsible]="false" class="mb-4">
          <div class="flex flex-col">
            <div>Select the workflow to use for this eviction.</div>
            @if (intakeEvictionFormService.workflows$ | async; as workflows) {
              <div class="mt-2 flex flex-col flex-1">
                <mat-list class="styled-list">
                  @for (workflow of workflows; track workflow) {
                    <mat-list-item
                      (click)="selectWorkflow(workflow)"
                      class="client-row"
                      [ngClass]="{
                        selected: workflow?.id === intakeEvictionFormService.workflowDetails?.controls.workflow_id.value
                      }">
                      <div mat-line class="flex flex-row justify-between items-center">
                        <div class="primary">{{ workflow?.name }}</div>
                        <div class="secondary">{{ workflow?.step_count }} Steps</div>
                      </div>
                    </mat-list-item>
                  }
                </mat-list>
              </div>
            }
          </div>
        </ee-expansion-panel>
      }

      @if (attorneyUsers$ | async; as attorneyUsers) {
        <ee-expansion-panel header="Case Assignment" [collapsible]="false" class="mb-4">
          <div class="flex flex-row justify-center align-middle">
            @if (attorneyUsers.length) {
              <mat-form-field class="compact self-center" appearance="outline">
                <mat-label>Attorney Assigned to Case</mat-label>
                <mat-select
                  [(ngModel)]="assignedAttorneyUserId"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="selectAssignedAttorney($event)">
                  <mat-option [value]="undefined">-- None --</mat-option>
                  @for (attorney of attorneyUsers; track attorney) {
                    <mat-option [value]="attorney.id">{{ attorney.first_name }} {{ attorney.last_name }}</mat-option>
                  }
                </mat-select>
                <mat-hint>Optional</mat-hint>
              </mat-form-field>
            } @else {
              One or more users with an Attorney role required.
            }
          </div>
        </ee-expansion-panel>
      }
    </div>
  `,
  styles: [
    `
      .custom-fields {
        display: grid !important;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        grid-auto-rows: 1fr;
        grid-column-gap: 1rem;
        grid-row-gap: 1rem;
        overflow: hidden;
      }

      .highlighted-box {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
        background-color: var(--color-surface-container-high, #f5f5f5);
        padding: 8px 8px 0;

        &:last-of-type {
          padding-bottom: 8px;
        }

        .highlighted-box-label {
          text-transform: capitalize;
          font-weight: 600;
          font-size: 16px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          margin-bottom: 10px;

          .highlighted-box-sub-label {
            font-size: 12px;
            font-weight: normal;
          }
        }
      }
    `
  ],
  standalone: false
})
export class WorkflowFormComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('clientSelect', { static: false }) clientSelect: ElementRef<ClientSelectComponent>;

  @Output() addClient: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() workflowSelected: EventEmitter<Workflow> = new EventEmitter<Workflow>();

  @Input() cloneCase: Eviction | undefined;

  @Input() preSelectedClientId: string | undefined;

  subs: Subscription[] = [];
  query: string;
  accountTypes = AccountType;
  attorneyUsers$: Observable<User[]>;
  forms$: Observable<DocumentTemplate[]>;
  hasClients = true;
  assignedAttorneyUserId: string | undefined;
  public intakeStep: WorkflowStep | null;

  constructor(
    public intakeEvictionFormService: IntakeEvictionFormService,
    private userService: UserService,
    private formService: FormService,
    private clientService: ClientService
  ) {}

  ngOnInit() {
    this.attorneyUsers$ = this.userService.getUsers(null, true);
    this.forms$ = this.formService.getAllForms();
  }

  ngOnChanges() {
    this.autoSelectWorkflowByClientId(this.preSelectedClientId);
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }

  handleAddClient() {
    this.addClient.emit(false);
  }

  get selectedClient(): Organization {
    return this.intakeEvictionFormService.workflowDetails.controls.client_details.value;
  }

  selectClient(client: Organization) {
    this.intakeEvictionFormService.updateClient(client);
    this.intakeEvictionFormService.workflowDetails.controls.client_details.setValue(client);
    this.autoSelectWorkflow(client);
  }

  autoSelectWorkflowByClientId(clientId: string) {
    if (!clientId) {
      return;
    }

    this.subs.push(
      this.clientService.getClientSettings(clientId).subscribe((clientSettings: ClientSettings) => {
        // Wait for workflows to be loaded before selecting
        if (!this.intakeEvictionFormService.workflows || this.intakeEvictionFormService.workflows.length === 0) {
          // Subscribe to workflows$ to wait for workflows to be loaded
          // Use take(1) to ensure we only handle the first emission and prevent memory leaks
          this.subs.push(
            this.intakeEvictionFormService.workflows$.pipe(take(1)).subscribe((workflows: Workflow[]) => {
              if (workflows && workflows.length > 0) {
                this.selectWorkflowByPriority(clientSettings);
              }
            })
          );
          return;
        }
        this.selectWorkflowByPriority(clientSettings);
      })
    );
  }

  private selectWorkflowByPriority(clientSettings?: ClientSettings) {
    const workflows = this.intakeEvictionFormService.workflows;
    if (!workflows || workflows.length === 0) {
      return;
    }

    let selectedWorkflow: Workflow | undefined;

    // Priority 1: Client settings default_workflow (if it exists in workflows)
    if (clientSettings?.default_workflow) {
      selectedWorkflow = workflows.find((w) => w.id === clientSettings.default_workflow);
    }

    // Priority 2: Attorney default workflow (if client workflow not found)
    if (!selectedWorkflow) {
      const defaultWorkflowId = this.intakeEvictionFormService.defaultWorkflowId;
      if (defaultWorkflowId) {
        selectedWorkflow = workflows.find((w) => w.id === defaultWorkflowId);
      }
    }

    // Priority 3: First workflow in the list (fallback)
    if (!selectedWorkflow) {
      selectedWorkflow = workflows[0];
    }

    this.selectWorkflow(selectedWorkflow);
  }

  autoSelectWorkflow(client: Organization) {
    if (!client || !client.client_settings) {
      return;
    }

    const workflows = this.intakeEvictionFormService.workflows;
    if (!workflows || workflows.length === 0) {
      return;
    }

    let selectedWorkflow: Workflow | undefined;

    // Priority 1: Client settings default_workflow (if it exists in workflows)
    if (client.client_settings.default_workflow) {
      selectedWorkflow = workflows.find((w) => w.id === client.client_settings.default_workflow);
    }

    // Priority 2: Attorney default workflow (if client workflow not found)
    if (!selectedWorkflow) {
      const defaultWorkflowId = this.intakeEvictionFormService.defaultWorkflowId;
      if (defaultWorkflowId) {
        selectedWorkflow = workflows.find((w) => w.id === defaultWorkflowId);
      }
    }

    // Priority 3: First workflow in the list (fallback)
    if (!selectedWorkflow) {
      selectedWorkflow = workflows[0];
    }

    this.selectWorkflow(selectedWorkflow);
  }

  selectWorkflow(workflow: Workflow) {
    if (workflow) {
      this.intakeEvictionFormService.workflowDetails.controls.workflow_id.setValue(workflow.id);
      this.intakeStep = workflow.steps.filter((s) => s.step === 1)[0];

      if (workflow.assign_attorney_id) {
        this.selectAssignedAttorney(workflow.assign_attorney_id);
      } else {
        this.selectAssignedAttorney(undefined);
      }
      this.workflowSelected.emit(workflow);
    } else {
      this.intakeStep = undefined;
    }
  }

  selectAssignedAttorney(attorneyUserId: string | undefined) {
    this.assignedAttorneyUserId = attorneyUserId;
    this.intakeEvictionFormService.updateAttorneyUser(attorneyUserId);
  }

  updateClientCount(count: number) {
    this.hasClients = count > 0;
  }
}
